<template>
  <div class="image-uploader">
    <!-- Upload Area -->
    <div v-if="!previewUrl" class="upload-area"
         @click="triggerFileInput"
         @dragover.prevent="handleDragOver"
         @drop.prevent="handleFileDrop"
         @dragenter="handleDragEnter"
         @dragleave="handleDragLeave"
         :class="{ 'drag-over': isDragOver }">
      <div class="upload-icon">
        <font-awesome-icon icon="cloud-upload-alt" class="upload-icon-svg" />
      </div>
      <div class="upload-text">
        <h5>{{ title || $t('profile.uploadImage') }}</h5>
        <p>{{ description || $t('profile.dragDropOrClick') }}</p>
        <small class="text-muted">{{ $t('profile.supportedFormats') }}</small>
      </div>
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        @change="handleFileSelect"
        style="display: none"
      />
    </div>

    <!-- Preview Area -->
    <div v-else class="preview-area">
      <div class="preview-header">
        <h6>{{ $t('profile.selectedFile') }}</h6>
        <button @click="clearImage" class="btn-remove-file">
          <font-awesome-icon icon="times" />
        </button>
      </div>
      <div class="preview-content">
        <img :src="previewUrl" :alt="selectedFile.name" class="preview-image" />
        <div class="file-info">
          <span class="file-name">{{ selectedFile.name }}</span>
          <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <font-awesome-icon icon="spinner" class="fa-spin fa-2x text-primary" />
      <p class="mt-2">{{ loadingText || $t('profile.uploadingImage') }}</p>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="alert alert-danger">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImageUploader',
  props: {
    title: {
      type: String,
      default: null
    },
    description: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingText: {
      type: String,
      default: null
    },
    error: {
      type: String,
      default: null
    },
    maxSize: {
      type: Number,
      default: 5 * 1024 * 1024 // 5MB default
    },
    allowedTypes: {
      type: Array,
      default: () => ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
    },
    allowedExtensions: {
      type: Array,
      default: () => ['png', 'jpg', 'jpeg', 'gif', 'webp']
    }
  },
  data () {
    return {
      selectedFile: null,
      previewUrl: '',
      isDragOver: false
    }
  },
  methods: {
    triggerFileInput () {
      this.$refs.fileInput.click()
    },

    handleFileSelect (event) {
      const file = event.target.files[0]
      if (file) {
        this.processSelectedFile(file)
      }
    },

    handleFileDrop (event) {
      const files = event.dataTransfer.files
      if (files.length > 0) {
        this.processSelectedFile(files[0])
      }
    },

    handleDragOver (event) {
      event.preventDefault()
      this.isDragOver = true
    },

    handleDragEnter (event) {
      event.preventDefault()
      this.isDragOver = true
    },

    handleDragLeave (event) {
      event.preventDefault()
      this.isDragOver = false
    },

    processSelectedFile (file) {
      // Validate file
      if (!this.validateFile(file)) {
        return
      }

      this.selectedFile = file
      this.$emit('file-selected', file)

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        this.previewUrl = e.target.result
      }
      reader.readAsDataURL(file)
    },

    validateFile (file) {
      if (!file) {
        this.$emit('error', this.$t('profile.noFileSelected'))
        return false
      }

      // Check file extension
      const fileExtension = file.name.split('.').pop().toLowerCase()
      if (!this.allowedExtensions.includes(fileExtension)) {
        this.$emit('error', `Please select a valid image file (${this.allowedExtensions.join(', ').toUpperCase()})`)
        return false
      }

      // Check MIME type
      if (!this.allowedTypes.includes(file.type)) {
        this.$emit('error', 'Please select an image file')
        return false
      }

      // Check file size
      if (file.size > this.maxSize) {
        const maxSizeMB = Math.round(this.maxSize / (1024 * 1024))
        this.$emit('error', this.$t('profile.fileSizeError') || `File size must be less than ${maxSizeMB}MB`)
        return false
      }

      return true
    },

    clearImage () {
      this.selectedFile = null
      this.previewUrl = ''
      this.isDragOver = false
      this.$refs.fileInput.value = ''
      this.$emit('file-cleared')
    },

    formatFileSize (bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    // Public method to get selected file
    getSelectedFile () {
      return this.selectedFile
    },

    // Public method to reset component
    reset () {
      this.clearImage()
      this.$emit('error', null)
    }
  }
}
</script>

<style lang="scss" scoped>
.image-uploader {
  width: 100%;
}

.upload-area {
  border: 2px dashed #dee2e6;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);

  &:hover {
    border-color: #4a90e2;
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(74, 144, 226, 0.15);
  }

  &.drag-over {
    border-color: #4a90e2;
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    transform: scale(1.02);
    box-shadow: 0 6px 20px rgba(74, 144, 226, 0.25);
  }
}

.upload-icon {
  margin-bottom: 1rem;
}

.upload-icon-svg {
  font-size: 3rem;
  color: #4a90e2;
  transition: all 0.3s ease;
}

.upload-area:hover .upload-icon-svg {
  transform: scale(1.1);
  color: #357abd;
}

.upload-text h5 {
  color: #2c3e50;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.upload-text p {
  color: #7f8c8d;
  margin-bottom: 0.5rem;
}

.preview-area {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.preview-header h6 {
  color: #2c3e50;
  font-weight: 600;
  margin: 0;
}

.btn-remove-file {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: #e74c3c;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.8rem;

  &:hover {
    background: #c0392b;
    transform: scale(1.1);
  }
}

.preview-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.preview-image {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e9ecef;
}

.file-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.file-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.file-size {
  color: #7f8c8d;
  font-size: 0.8rem;
}

.loading-state {
  text-align: center;
  padding: 2rem;
}

// Responsive styles
@media (max-width: 768px) {
  .upload-area {
    padding: 1.5rem;
  }

  .upload-icon-svg {
    font-size: 2.5rem;
  }

  .upload-text h5 {
    font-size: 1.1rem;
  }

  .preview-content {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }

  .preview-image {
    width: 80px;
    height: 80px;
  }
}
</style>
